import { Role } from "@/config/permission/role";

type AppEnv = "development" | "staging" | "production";

/**
 * Temporary hardcoded location IDs for TS-TMC provider restriction.
 * TODO: Replace with backend feature-flag configuration after rollout is stable.
 */
export const TS_TMC_LOCATION_ID_BY_ENV: Record<AppEnv, string> = {
  development: "08de7a7f-d5ec-4748-8e70-dc274a83d233",
  staging: "08de7a83-79f8-4597-8774-8c51353aa634",
  production: "08de96ab-7748-47b4-8cff-9fcb155254d6",
};

type TsTmcLocationIdOverrides = Partial<Record<AppEnv, string>>;
const DOCTOR_SCOPE_ROLE_SET = new Set(
  [Role.Doctor, Role.DoctorAdmin].map((role) => role.toLowerCase()),
);

export interface TsTmcProviderRestrictionPolicy {
  enabled: boolean;
  hidePatientList: boolean;
  lockQueueToTaggedProvider: boolean;
  hideQueueDoctorFilter: boolean;
}

const normalizeEnv = (rawEnv: string): AppEnv => {
  const env = rawEnv.toLowerCase();

  if (env.includes("prod")) {
    return "production";
  }

  if (env.includes("stag") || env.includes("stg")) {
    return "staging";
  }

  return "development";
};

export const getCurrentAppEnv = (): AppEnv => {
  const envBag = (import.meta as ImportMeta & { env?: Record<string, string | undefined> }).env;
  const env = envBag?.VITE_ENV || envBag?.MODE || "development";
  return normalizeEnv(env);
};

const getRuntimeOverrideLocationIds = (): TsTmcLocationIdOverrides => {
  const envBag = (import.meta as ImportMeta & { env?: Record<string, string | undefined> }).env;

  return {
    development: envBag?.VITE_TS_TMC_LOCATION_ID_DEVELOPMENT || envBag?.VITE_TS_TMC_LOCATION_ID_DEV,
    staging: envBag?.VITE_TS_TMC_LOCATION_ID_STAGING || envBag?.VITE_TS_TMC_LOCATION_ID_STG,
    production: envBag?.VITE_TS_TMC_LOCATION_ID_PRODUCTION || envBag?.VITE_TS_TMC_LOCATION_ID_PROD,
  };
};

export const buildTsTmcLocationIdMap = (
  overrides: TsTmcLocationIdOverrides = getRuntimeOverrideLocationIds(),
): Record<AppEnv, string> => {
  return {
    development: overrides.development || TS_TMC_LOCATION_ID_BY_ENV.development,
    staging: overrides.staging || TS_TMC_LOCATION_ID_BY_ENV.staging,
    production: overrides.production || TS_TMC_LOCATION_ID_BY_ENV.production,
  };
};

export const getTsTmcLocationId = (
  env: AppEnv = getCurrentAppEnv(),
  overrides?: TsTmcLocationIdOverrides,
): string => {
  return buildTsTmcLocationIdMap(overrides)[env];
};

export const isDoctorOrDoctorAdminRole = (roleLabel?: string | null): boolean => {
  const normalizedRole = roleLabel?.trim().toLowerCase();

  if (!normalizedRole) {
    return false;
  }

  return DOCTOR_SCOPE_ROLE_SET.has(normalizedRole);
};

export const isTsTmcProviderRestrictionEnabled = ({
  locationId,
  roleLabel,
  env = getCurrentAppEnv(),
  overrides,
}: {
  locationId?: string | null;
  roleLabel?: string | null;
  env?: AppEnv;
  overrides?: TsTmcLocationIdOverrides;
}): boolean => {
  if (!locationId) {
    return false;
  }

  return locationId === getTsTmcLocationId(env, overrides) && isDoctorOrDoctorAdminRole(roleLabel);
};

export const getTsTmcProviderRestrictionPolicy = ({
  locationId,
  roleLabel,
  env = getCurrentAppEnv(),
  overrides,
}: {
  locationId?: string | null;
  roleLabel?: string | null;
  env?: AppEnv;
  overrides?: TsTmcLocationIdOverrides;
}): TsTmcProviderRestrictionPolicy => {
  const enabled = isTsTmcProviderRestrictionEnabled({
    locationId,
    roleLabel,
    env,
    overrides,
  });

  return {
    enabled,
    hidePatientList: enabled,
    lockQueueToTaggedProvider: enabled,
    hideQueueDoctorFilter: enabled,
  };
};
